import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  addDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';

// Collections
const COLLECTIONS = {
  CUSTOMERS: 'customers',
  PRODUCTS: 'products',
  BILLS: 'bills',
  TREASURY: 'treasury',
  TRANSACTIONS: 'transactions'
};

// ============ CUSTOMERS / FARMERS ============
export const saveCustomer = async (customer) => {
  try {
    console.log('Saving customer to Firestore:', customer.name);
    const customerRef = doc(db, COLLECTIONS.CUSTOMERS, customer.phone);
    await setDoc(customerRef, {
      ...customer,
      updatedAt: serverTimestamp()
    }, { merge: true });
    console.log('✅ Customer saved successfully:', customer.name);
    return { success: true };
  } catch (error) {
    console.error('❌ Error saving customer:', error.message);
    console.error('Full error:', error);
    return { success: false, error };
  }
};

export const getAllCustomers = async () => {
  try {
    console.log('Loading customers from Firestore...');
    const customersSnapshot = await getDocs(collection(db, COLLECTIONS.CUSTOMERS));
    const customers = [];
    customersSnapshot.forEach((doc) => {
      customers.push({ id: doc.id, ...doc.data() });
    });
    console.log(`✅ Loaded ${customers.length} customers from Firestore`);
    return customers;
  } catch (error) {
    console.error('❌ Error getting customers:', error.message);
    console.error('Full error:', error);
    return [];
  }
};

export const getCustomerByPhone = async (phone) => {
  try {
    const customerRef = doc(db, COLLECTIONS.CUSTOMERS, phone);
    const customerSnap = await getDoc(customerRef);
    if (customerSnap.exists()) {
      return { id: customerSnap.id, ...customerSnap.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting customer:', error);
    return null;
  }
};

export const updateCustomer = async (phone, updates) => {
  try {
    const customerRef = doc(db, COLLECTIONS.CUSTOMERS, phone);
    await updateDoc(customerRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating customer:', error);
    return { success: false, error };
  }
};

export const deleteCustomer = async (phone) => {
  try {
    console.log('Deleting customer from Firestore:', phone);
    const customerRef = doc(db, COLLECTIONS.CUSTOMERS, phone);
    await deleteDoc(customerRef);
    console.log('✅ Customer deleted successfully:', phone);
    return { success: true };
  } catch (error) {
    console.error('❌ Error deleting customer:', error.message);
    return { success: false, error };
  }
};

// ============ PRODUCTS ============
export const saveProduct = async (product) => {
  try {
    console.log('Saving product to Firestore:', product.name);
    const productRef = doc(db, COLLECTIONS.PRODUCTS, product.id.toString());
    await setDoc(productRef, {
      ...product,
      updatedAt: serverTimestamp()
    });
    console.log('✅ Product saved successfully:', product.name);
    return { success: true };
  } catch (error) {
    console.error('❌ Error saving product:', error.message);
    console.error('Full error:', error);
    return { success: false, error };
  }
};

export const getAllProducts = async () => {
  try {
    const productsSnapshot = await getDocs(collection(db, COLLECTIONS.PRODUCTS));
    const products = [];
    productsSnapshot.forEach((doc) => {
      products.push({ ...doc.data() });
    });
    return products;
  } catch (error) {
    console.error('Error getting products:', error);
    return [];
  }
};

export const updateProduct = async (productId, updates) => {
  try {
    const productRef = doc(db, COLLECTIONS.PRODUCTS, productId.toString());
    await updateDoc(productRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating product:', error);
    return { success: false, error };
  }
};

export const deleteProduct = async (productId) => {
  try {
    const productRef = doc(db, COLLECTIONS.PRODUCTS, productId.toString());
    await deleteDoc(productRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting product:', error);
    return { success: false, error };
  }
};

// ============ BILLS ============
export const saveBill = async (bill) => {
  try {
    console.log('Saving bill to Firestore:', bill.billNumber);
    const billRef = await addDoc(collection(db, COLLECTIONS.BILLS), {
      ...bill,
      createdAt: serverTimestamp()
    });
    console.log('✅ Bill saved successfully:', bill.billNumber);
    return { success: true, id: billRef.id };
  } catch (error) {
    console.error('❌ Error saving bill:', error.message);
    console.error('Full error:', error);
    return { success: false, error };
  }
};

export const getAllBills = async () => {
  try {
    const billsSnapshot = await getDocs(
      query(collection(db, COLLECTIONS.BILLS), orderBy('createdAt', 'desc'))
    );
    const bills = [];
    billsSnapshot.forEach((doc) => {
      bills.push({ id: doc.id, ...doc.data() });
    });
    return bills;
  } catch (error) {
    console.error('Error getting bills:', error);
    return [];
  }
};

export const getBillsByCustomer = async (phone) => {
  try {
    const billsQuery = query(
      collection(db, COLLECTIONS.BILLS),
      where('phone', '==', phone),
      orderBy('createdAt', 'desc')
    );
    const billsSnapshot = await getDocs(billsQuery);
    const bills = [];
    billsSnapshot.forEach((doc) => {
      bills.push({ id: doc.id, ...doc.data() });
    });
    return bills;
  } catch (error) {
    console.error('Error getting customer bills:', error);
    return [];
  }
};

// ============ TREASURY TRANSACTIONS ============
export const saveTreasuryTransaction = async (transaction) => {
  try {
    const transactionRef = await addDoc(collection(db, COLLECTIONS.TREASURY), {
      ...transaction,
      createdAt: serverTimestamp()
    });
    return { success: true, id: transactionRef.id };
  } catch (error) {
    console.error('Error saving treasury transaction:', error);
    return { success: false, error };
  }
};

export const getAllTreasuryTransactions = async () => {
  try {
    const transactionsSnapshot = await getDocs(
      query(collection(db, COLLECTIONS.TREASURY), orderBy('timestamp', 'desc'))
    );
    const transactions = [];
    transactionsSnapshot.forEach((doc) => {
      transactions.push({ id: doc.id, ...doc.data() });
    });
    return transactions;
  } catch (error) {
    console.error('Error getting treasury transactions:', error);
    return [];
  }
};

export const updateTreasuryTransaction = async (transactionId, updates) => {
  try {
    const transactionRef = doc(db, COLLECTIONS.TREASURY, transactionId.toString());
    await updateDoc(transactionRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating treasury transaction:', error);
    return { success: false, error };
  }
};

// ============ SYNC ALL DATA ============
export const syncAllData = async (data) => {
  try {
    // Sync customers
    if (data.customers && data.customers.length > 0) {
      for (const customer of data.customers) {
        await saveCustomer(customer);
      }
    }

    // Sync products
    if (data.products && data.products.length > 0) {
      for (const product of data.products) {
        await saveProduct(product);
      }
    }

    return { success: true };
  } catch (error) {
    console.error('Error syncing all data:', error);
    return { success: false, error };
  }
};

// ============ LOAD ALL DATA ============
export const loadAllData = async () => {
  try {
    const [customers, products, bills, treasuryTransactions] = await Promise.all([
      getAllCustomers(),
      getAllProducts(),
      getAllBills(),
      getAllTreasuryTransactions()
    ]);

    return {
      customers,
      products,
      bills,
      treasuryTransactions
    };
  } catch (error) {
    console.error('Error loading all data:', error);
    return {
      customers: [],
      products: [],
      bills: [],
      treasuryTransactions: []
    };
  }
};
